import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  ButtonGroup,
} from "reactstrap";
import Select from "react-select";
import InputMask from "react-input-mask";
import { toast } from "react-toastify";
import FeatherIcon from "feather-icons-react";
import { APIClient } from "../../../helpers/api_helper";

const apiClient = new APIClient();

// AIDEV-NOTE: Enhanced ModalFornecedor with dual functionality (manual/import) and CPF/CNPJ toggle
const ModalFornecedor = ({ isOpen, toggle, onFornecedorAdded, listaClientes = [] }) => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("manual");
  
  // Manual registration states
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [documento, setDocumento] = useState("");
  const [tipoDocumento, setTipoDocumento] = useState("cpf"); // cpf or cnpj
  
  // Client import states
  const [clienteSelecionado, setClienteSelecionado] = useState(null);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle client selection
  const handleClienteChange = (selectedOption) => {
    setClienteSelecionado(selectedOption);
    if (selectedOption?.data) {
      const cliente = selectedOption.data;
      setNome(cliente.nome_completo || cliente.name || "");
      setEmail(cliente.email || "");
      setTelefone(cliente.telefone || "");
      setDocumento(cliente.cpf_cnpj || "");
      // Auto-detect document type
      const doc = cliente.cpf_cnpj?.replace(/\D/g, '') || "";
      setTipoDocumento(doc.length <= 11 ? "cpf" : "cnpj");
    }
  };

  // Get document mask based on type
  const getDocumentMask = () => {
    return tipoDocumento === "cpf" ? "999.999.999-99" : "99.999.999/9999-99";
  };

  const getDocumentPlaceholder = () => {
    return tipoDocumento === "cpf" ? "000.000.000-00" : "00.000.000/0000-00";
  };

  // Handle manual form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!nome.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    
    if (!documento.trim()) {
      toast.error(`${tipoDocumento.toUpperCase()} é obrigatório`);
      return;
    }

    setLoading(true);
    
    try {
      const fornecedorData = {
        nome: nome.trim(),
        email: email.trim() || `${nome.toLowerCase().replace(/\s/g, '')}@fornecedor.com`,
        telefone: telefone.trim() || "0000000000",
        cpf_cnpj: documento.trim(),
        tipo: "fornecedor"
      };

      if (activeTab === "importar" && clienteSelecionado?.data) {
        fornecedorData.cliente_origem_id = clienteSelecionado.data.user_id || clienteSelecionado.data.id;
      }

      const response = await apiClient.post("/fornecedores", fornecedorData);

      if (response.sucesso) {
        toast.success("Fornecedor cadastrado com sucesso!");
        
        // Notificar o componente pai
        if (onFornecedorAdded) {
          onFornecedorAdded(response.dados);
        }
        
        handleClose();
      } else {
        toast.error(response.mensagem || "Erro ao cadastrar fornecedor");
      }
    } catch (error) {
      toast.error("Erro ao cadastrar fornecedor");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset all states
      setNome("");
      setEmail("");
      setTelefone("");
      setDocumento("");
      setTipoDocumento("cpf");
      setClienteSelecionado(null);
      setActiveTab("manual");
      toggle();
    }
  };

  const isFormValid = nome.trim() && documento.trim();

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        <div className="d-flex align-items-center">
          <FeatherIcon icon="user-plus" className="me-2" size={20} />
          Cadastrar Novo Fornecedor
        </div>
      </ModalHeader>
      
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Tabs Navigation */}
          <Nav tabs className="mb-3">
            <NavItem>
              <NavLink
                className={activeTab === "manual" ? "active" : ""}
                onClick={() => handleTabChange("manual")}
                style={{ cursor: "pointer" }}
              >
                <FeatherIcon icon="edit-3" size={16} className="me-1" />
                Cadastrar Manualmente
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={activeTab === "importar" ? "active" : ""}
                onClick={() => handleTabChange("importar")}
                style={{ cursor: "pointer" }}
              >
                <FeatherIcon icon="users" size={16} className="me-1" />
                Importar de Cliente
              </NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            {/* Manual Registration Tab */}
            <TabPane tabId="manual">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <FormGroup>
                    <Label for="nome">Nome do Fornecedor *</Label>
                    <Input
                      type="text"
                      id="nome"
                      value={nome}
                      onChange={(e) => setNome(e.target.value)}
                      placeholder="Digite o nome do fornecedor"
                      maxLength={255}
                      disabled={loading}
                      required
                    />
                  </FormGroup>
                </div>
                
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="fornecedor@email.com"
                      disabled={loading}
                    />
                    <small className="text-muted">
                      Se não informado, será gerado automaticamente
                    </small>
                  </FormGroup>
                </div>
                
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="telefone">Telefone</Label>
                    <InputMask
                      mask="(99) 99999-9999"
                      value={telefone}
                      onChange={(e) => setTelefone(e.target.value)}
                      disabled={loading}
                    >
                      {(inputProps) => (
                        <Input
                          {...inputProps}
                          type="text"
                          id="telefone"
                          placeholder="(11) 99999-9999"
                        />
                      )}
                    </InputMask>
                  </FormGroup>
                </div>
                
                <div className="col-md-12">
                  <FormGroup>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Label for="documento">Documento *</Label>
                      <ButtonGroup size="sm">
                        <Button
                          color={tipoDocumento === "cpf" ? "primary" : "outline-primary"}
                          onClick={() => {
                            setTipoDocumento("cpf");
                            setDocumento("");
                          }}
                          disabled={loading}
                        >
                          CPF
                        </Button>
                        <Button
                          color={tipoDocumento === "cnpj" ? "primary" : "outline-primary"}
                          onClick={() => {
                            setTipoDocumento("cnpj");
                            setDocumento("");
                          }}
                          disabled={loading}
                        >
                          CNPJ
                        </Button>
                      </ButtonGroup>
                    </div>
                    <InputMask
                      mask={getDocumentMask()}
                      value={documento}
                      onChange={(e) => setDocumento(e.target.value)}
                      disabled={loading}
                    >
                      {(inputProps) => (
                        <Input
                          {...inputProps}
                          type="text"
                          id="documento"
                          placeholder={getDocumentPlaceholder()}
                          required
                        />
                      )}
                    </InputMask>
                  </FormGroup>
                </div>
              </div>
            </TabPane>

            {/* Import from Client Tab */}
            <TabPane tabId="importar">
              <FormGroup>
                <Label>Selecionar Cliente</Label>
                <Select
                  value={clienteSelecionado}
                  onChange={handleClienteChange}
                  options={listaClientes}
                  placeholder="Busque e selecione um cliente..."
                  isSearchable
                  isClearable
                  noOptionsMessage={() => "Nenhum cliente encontrado"}
                />
                <small className="text-muted mt-1 d-block">
                  Os dados do cliente selecionado serão importados automaticamente
                </small>
              </FormGroup>

              {clienteSelecionado && (
                <div className="mt-3 p-3 bg-light rounded">
                  <h6 className="mb-2">
                    <FeatherIcon icon="eye" size={16} className="me-1" />
                    Prévia dos Dados
                  </h6>
                  <div className="row">
                    <div className="col-md-6">
                      <small className="text-muted">Nome:</small>
                      <div className="fw-medium">{nome || "N/A"}</div>
                    </div>
                    <div className="col-md-6">
                      <small className="text-muted">Email:</small>
                      <div className="fw-medium">{email || "N/A"}</div>
                    </div>
                    <div className="col-md-6 mt-2">
                      <small className="text-muted">Telefone:</small>
                      <div className="fw-medium">{telefone || "N/A"}</div>
                    </div>
                    <div className="col-md-6 mt-2">
                      <small className="text-muted">Documento:</small>
                      <div className="fw-medium">{documento || "N/A"}</div>
                    </div>
                  </div>
                </div>
              )}
            </TabPane>
          </TabContent>
        </ModalBody>
        
        <ModalFooter>
          <Button 
            type="button" 
            color="secondary" 
            onClick={handleClose}
            disabled={loading}
          >
            <FeatherIcon icon="x" size={16} className="me-1" />
            Cancelar
          </Button>
          <Button 
            type="submit" 
            color="primary"
            disabled={loading || !isFormValid}
          >
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Cadastrando...
              </>
            ) : (
              <>
                <FeatherIcon icon="check" size={16} className="me-1" />
                Cadastrar Fornecedor
              </>
            )}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ModalFornecedor; 